/**
 * HumanHandoffBriefingPage — DIGEMP-HUMAN-HANDOFF-FRONTEND-058, route /handoffs/:runId.
 *
 * Loads one handed-off run (and, when the run carries context.conversation_id, the conversation
 * it belongs to) and renders the packaged HandoffBriefing. The conversation fetch is optional:
 * if it fails the briefing still renders and says the conversation context is missing instead of
 * silently dropping it.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { agentRunsApi, conversationsApi } from "../services/api";
import { useAgentPermissions } from "../context/AgentPermissionsContext";
import HandoffBriefing from "../components/HandoffBriefing";

export default function HumanHandoffBriefingPage() {
  const { runId } = useParams();
  const { can: canAgent } = useAgentPermissions();
  const canView = canAgent("agents_view");

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["handoff-briefing", runId],
    queryFn: async () => {
      const run = await agentRunsApi.get(runId);
      const conversationId = run?.context?.conversation_id;
      if (!conversationId) return { run, conversation: null, conversationError: false };
      try {
        const conversation = await conversationsApi.get(conversationId);
        return { run, conversation, conversationError: false };
      } catch {
        return { run, conversation: null, conversationError: true };
      }
    },
    enabled: canView && Boolean(runId),
    retry: false,
  });

  if (!canView) {
    return <div className="p-6 text-gray-500">אין הרשאה לצפות במסירה לאדם</div>;
  }

  const notFound = error?.response?.status === 404;

  return (
    <div className="w-full max-w-[900px] mx-auto px-4 py-6 space-y-4" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-gray-800">תדריך מסירה</h1>
        <Link to="/handoffs" className="text-sm text-blue-600 hover:underline">→ חזרה למסירות</Link>
      </div>

      {isLoading && <p className="text-gray-400 text-sm">טוען תדריך...</p>}

      {isError && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
          {notFound ? "המסירה לא נמצאה — ייתכן שהריצה נמחקה או שהקישור שגוי." : "שגיאה בטעינת התדריך"}
          {!notFound && (
            <button type="button" onClick={() => refetch()} className="mr-2 underline">נסה שוב</button>
          )}
        </div>
      )}

      {data?.run && (
        <HandoffBriefing
          run={data.run}
          conversation={data.conversation}
          conversationError={data.conversationError}
        />
      )}
    </div>
  );
}
